import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { useClickOutsideAndEscape } from 'hooks'
import { InputOptions } from 'types/input'
import DropdownButton from './dropdownButton'

export type DropdownControlProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  options: InputOptions[]
  label?: string
  error?: string
  hintText?: string
  parentClassName?: string
  className?: string
  required?: boolean
  size?: 'small' | 'medium'
  searchable?: boolean
}

const DropdownControl = <T extends FieldValues>({
  name,
  control,
  options,
  label,
  error,
  hintText,
  parentClassName = '',
  className,
  required = false,
  size = 'medium',
  searchable = true,
}: DropdownControlProps<T>) => {
  const { field } = useController({ name, control })

  const [isVisible, setIsVisible] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const ref = useRef<HTMLDivElement | null>(null)

  useClickOutsideAndEscape({
    ref,
    callback: () => {
      setIsVisible(false)
      setSearchQuery('')
    },
  })

  useEffect(() => {
    if (isVisible && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isVisible, searchable])

  const selected = useMemo(
    () => options.find((o) => o.value === field.value),
    [options, field.value],
  )

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options
    const q = searchQuery.toLowerCase()
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        String(o.value).toLowerCase().includes(q),
    )
  }, [options, searchQuery, searchable])

  const handleSelect = useCallback(
    (option: InputOptions) => {
      field.onChange(option.value)
      setIsVisible(false)
      setSearchQuery('')
    },
    [field.onChange],
  )

  return (
    <div className={`relative w-full outline-none ${parentClassName}`}>
      {label && (
        <label
          htmlFor={field.name}
          className="mb-1 text-xs font-semibold text-sidebar-primary"
        >
          {label}
          {required && <span className="text-error">*</span>}
        </label>
      )}

      <div ref={ref}>
        <DropdownButton
          className={className}
          setIsVisible={setIsVisible}
          isVisible={isVisible}
        >
          {selected?.label ?? 'Select'}
        </DropdownButton>

        <div
          className={`absolute z-10 h-max max-h-64 w-full min-w-max origin-top overflow-auto rounded-md bg-white shadow-lg transition-all duration-200 ${
            isVisible ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0'
          }`}
        >
          {searchable && options.length > 0 && (
            <div className="sticky top-0 border-b bg-white p-2">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-gray-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className="p-2">
            {filteredOptions.length === 0 ? (
              <div
                className={`block w-full rounded-md text-sm ${
                  size === 'small' ? 'px-1.5 py-1' : 'px-4 py-2.5'
                } text-left text-gray-400`}
              >
                No results found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  type="button"
                  key={String(option.value)}
                  className={`block w-full cursor-pointer rounded-md text-sm ${
                    size === 'small' ? 'px-1.5 py-1' : 'px-4 py-2.5'
                  } text-left outline-none hover:bg-slate-100 disabled:cursor-not-allowed disabled:bg-transparent disabled:opacity-30`}
                  onClick={() => handleSelect(option)}
                  disabled={option?.disabled}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {(error || hintText) && (
        <div>
          {error ? (
            <p className="mt-1 text-xs text-error">{error}</p>
          ) : (
            <p className="mt-1 text-[11px] text-gray-500">{hintText}</p>
          )}
        </div>
      )}
    </div>
  )
}

export default DropdownControl
